import React, { useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ArrowLeft,
  Edit2,
  Edit,
  Plus,
  Smartphone,
  Play,
  Trash2,
  Clock,
  Search,
  ChevronsLeft,
  ChevronsRight,
} from "react-feather";
import { useGet, usePost, usePut, useDelete } from "../../api";
import {
  IQaReviewList,
  IQaReviewListItem,
  IQaCaptureQueueResponse,
  IKindredDomainGeoResponse,
  FULL_AUTO_COUPONS_TARGETING_FLAG,
} from "../../interfaces";
import { settingsType, useSettings } from "../../hooks";
import {
  EditSessionProvider,
  useEditSession,
  ReviewDetailPanel,
} from "../../components/workbench";
import { ReviewItemList } from "./review-item-list";
import { ReviewDataPanel } from "./review-data-panel";
import { ReviewSubmitModal } from "./review-submit-modal";
import { CreateEntityModal } from "../../components/workbench";
import { DomainGeoPicker, DomainGeoItem } from "./domain-geo-picker";


/**
 * The schedule is stored as cron because that is what the scheduler reads, but
 * nobody should have to write one. These convert between the two, and only
 * handle the "midnight on chosen days" shape the picker offers — a cron typed
 * elsewhere (an hourly one, say) is shown as-is and left alone.
 */
const CAPTURE_DAYS = [
  { cron: 1, label: "Mon" },
  { cron: 2, label: "Tue" },
  { cron: 3, label: "Wed" },
  { cron: 4, label: "Thu" },
  { cron: 5, label: "Fri" },
  { cron: 6, label: "Sat" },
  { cron: 0, label: "Sun" },
];

const daysToCron = (days: number[]): string =>
  `0 0 * * ${[...days].sort((a, b) => a - b).join(",")}`;

/** Default audit hour (UTC), clear of the midnight live sweep's browser pool. */
const DEFAULT_AUDIT_HOUR = 8;

const auditDaysToCron = (days: number[], hour: number): string =>
  `0 ${hour} * * ${[...days].sort((a, b) => a - b).join(",")}`;

const parseAuditCron = (
  cron?: string | null
): { days: number[]; hour: number } | null => {
  const m = /^0 (\d{1,2}) \* \* ([0-7](?:,[0-7])*)$/.exec(
    String(cron ?? "").trim()
  );
  if (!m || Number(m[1]) > 23) return null;
  const days = m[2].split(",").map((d) => (Number(d) === 7 ? 0 : Number(d)));
  return { days: Array.from(new Set(days)), hour: Number(m[1]) };
};

const cronToDays = (cron?: string | null): number[] | null => {
  const m = /^0 0 \* \* ([0-7](?:,[0-7])*)$/.exec(String(cron ?? "").trim());
  if (!m) return null;
  // Array.from rather than spreading the Set — this package targets ES5.
  const days = m[1].split(",").map((d) => (Number(d) === 7 ? 0 : Number(d)));
  return Array.from(new Set(days));
};

const QaReviewInner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data: list,
    loading,
    refetch: refetchList,
  } = useGet<IQaReviewList>(`qa-lists/${id}`, true);

  // Panel state
  const [selectedItem, setSelectedItem] = useState<IQaReviewListItem | null>(
    null
  );
  const [selectedEntity, setSelectedEntity] = useState<{
    type: "deal" | "coupon" | "offer";
    id: string;
  } | null>(null);
  const [isListPanelCollapsed, setIsListPanelCollapsed] = useState(false);

  // Filters
  const [searchFilter, setSearchFilter] = useSettings(
    settingsType.QA_REVIEW_SEARCH
  );
  const [countryFilter, setCountryFilter] = useSettings(
    settingsType.QA_REVIEW_COUNTRY
  );
  const [untestedOnly, setUntestedOnly] = useSettings(
    settingsType.QA_REVIEW_UNTESTED_ONLY
  );
  const [notTestedDays, setNotTestedDays] = useSettings(
    settingsType.QA_REVIEW_NOT_TESTED_DAYS
  );

  // Mark tested
  const [markingTested, setMarkingTested] = useState<string | null>(null);
  const markTestedPost = usePost<{ success: boolean }, { comment: string }>();

  // Domain+geo data
  const domainGeoUrl = selectedItem
    ? `kindred/by-domain-geo?domain=${encodeURIComponent(selectedItem.domain)}${selectedItem.countryCode ? `&countryCode=${encodeURIComponent(selectedItem.countryCode)}` : ""}`
    : null;
  const { data: domainGeoData, loading: domainGeoLoading } =
    useGet<IKindredDomainGeoResponse>(domainGeoUrl, !!selectedItem);

  // Review modal
  const [showReview, setShowReview] = useState(false);
  const { editCount } = useEditSession();

  // Create entity modal
  const [showCreate, setShowCreate] = useState(false);

  // Edit list modal
  const [showEditList, setShowEditList] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const renamePut = usePut<IQaReviewList, { name: string }>();
  const [newItems, setNewItems] = useState<DomainGeoItem[]>([]);
  const [removingItem, setRemovingItem] = useState<string | null>(null);
  const [itemSearch, setItemSearch] = useState("");
  const addItemsPost = usePost<{ success: boolean }, any>();
  const deleteItemReq = useDelete<{ success: boolean }>();
  const setFlagsPut = usePut<{ flags: string[] }, { flags: string[] }>();
  const bulkAddFlagPut = usePut<{ modified: number }, { flag: string }>();
  const [bulkFlagging, setBulkFlagging] = useState(false);

  // Queue the map template capture for every item in the list.
  const captureAllPost = usePost<IQaCaptureQueueResponse, {}>();
  const [capturing, setCapturing] = useState(false);
  const [captureResult, setCaptureResult] =
    useState<IQaCaptureQueueResponse | null>(null);

  // Local Full-Auto flag filter
  const [fullAutoFlagFilter, setFullAutoFlagFilter] = useState<
    "" | "flagged" | "unflagged"
  >("");

  // Capture cadence for this list, overriding the global TEMPLATE_CAPTURE_CRON.
  const captureCronPut = usePut<any, { captureCron: string }>();
  const [savingCron, setSavingCron] = useState(false);
  const listCron = (list as any)?.captureCron as string | null | undefined;
  const cronDays = cronToDays(listCron);
  const customCron = !!listCron && cronDays === null;

  const saveCaptureCron = useCallback(
    async (cron: string) => {
      if (!id) return;
      setSavingCron(true);
      try {
        await captureCronPut.execute(`qa-lists/${id}`, { captureCron: cron });
        refetchList();
      } catch (err) {
        console.error("Failed to save the capture schedule:", err);
      } finally {
        setSavingCron(false);
      }
    },
    [id, captureCronPut, refetchList]
  );

  // Draft state: the modal edits locally and commits once on Save, rather than
  // firing a write per click.
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftDays, setDraftDays] = useState<number[]>([]);

  const openCaptureModal = useCallback(() => {
    setDraftEnabled(!!listCron);
    setDraftDays(cronDays ?? [1]);
    setShowCaptureModal(true);
  }, [listCron, cronDays]);

  const toggleDraftDay = useCallback((day: number) => {
    setDraftDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }, []);

  // Audit cadence — the same shape as capture, a different field and hour.
  const auditCronPut = usePut<any, { auditCron: string }>();
  const [savingAudit, setSavingAudit] = useState(false);
  const listAuditCron = (list as any)?.auditCron as string | null | undefined;
  const parsedAudit = parseAuditCron(listAuditCron);
  const auditDays = parsedAudit?.days ?? null;
  const customAuditCron = !!listAuditCron && parsedAudit === null;

  const [showAuditModal, setShowAuditModal] = useState(false);
  const [draftAuditEnabled, setDraftAuditEnabled] = useState(false);
  const [draftAuditDays, setDraftAuditDays] = useState<number[]>([]);
  const [draftAuditHour, setDraftAuditHour] = useState(DEFAULT_AUDIT_HOUR);

  const openAuditModal = useCallback(() => {
    const parsed = parseAuditCron(listAuditCron);
    setDraftAuditEnabled(!!listAuditCron);
    setDraftAuditDays(parsed?.days ?? [1]);
    setDraftAuditHour(parsed?.hour ?? DEFAULT_AUDIT_HOUR);
    setShowAuditModal(true);
  }, [listAuditCron]);

  const toggleDraftAuditDay = useCallback((day: number) => {
    setDraftAuditDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }, []);

  const saveAuditSchedule = useCallback(async () => {
    if (!id) return;
    const cron =
      draftAuditEnabled && draftAuditDays.length
        ? auditDaysToCron(draftAuditDays, draftAuditHour)
        : "";
    setSavingAudit(true);
    try {
      await auditCronPut.execute(`qa-lists/${id}`, { auditCron: cron });
      refetchList();
      setShowAuditModal(false);
    } catch (err) {
      console.error("Failed to save the audit schedule:", err);
    } finally {
      setSavingAudit(false);
    }
  }, [
    id,
    draftAuditEnabled,
    draftAuditDays,
    draftAuditHour,
    auditCronPut,
    refetchList,
  ]);

  const saveCaptureSchedule = useCallback(async () => {
    // On with no days selected is not a schedule — treat it as off rather than
    // writing a cron that never fires.
    const cron = draftEnabled && draftDays.length ? daysToCron(draftDays) : "";
    await saveCaptureCron(cron);
    setShowCaptureModal(false);
  }, [draftEnabled, draftDays, saveCaptureCron]);

  const handleBulkAddFullAutoFlag = useCallback(async () => {
    if (!id) return;
    setBulkFlagging(true);
    try {
      await bulkAddFlagPut.execute(`qa-lists/${id}/flags/bulk-add`, {
        flag: FULL_AUTO_COUPONS_TARGETING_FLAG,
      });
      refetchList();
    } catch (err) {
      console.error("Failed to bulk-add flag:", err);
    } finally {
      setBulkFlagging(false);
    }
  }, [id, bulkAddFlagPut, refetchList]);

  const handleQueueCaptures = useCallback(async () => {
    if (!id || !list) return;
    // This starts a browser walk per merchant, so it is worth a look before it
    // goes — there is no per-item undo once the scheduler claims them.
    const confirmed = window.confirm(
      `Queue the map template capture for all ${list.items.length} item${
        list.items.length === 1 ? "" : "s"
      } in "${list.name}"?\n\nEach one runs a full browser walk on the runner, and any domain/geo we have no merchant for is added as one.`
    );
    if (!confirmed) return;

    setCapturing(true);
    setCaptureResult(null);
    try {
      const result = await captureAllPost.execute(
        `qa-lists/${id}/capture`,
        {}
      );
      setCaptureResult(result);
    } catch (err) {
      console.error("Failed to queue captures:", err);
    } finally {
      setCapturing(false);
    }
  }, [id, list, captureAllPost]);

  // Audit all — the twin of Capture all. Records a one-shot request per
  // merchant; the scheduler claims them ahead of the scheduled sweep.
  const auditAllPost = usePost<
    {
      requested: number;
      queued: number;
      created: number;
      unmatched: number;
    },
    {}
  >();
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<{
    requested: number;
    queued: number;
    created: number;
    unmatched: number;
  } | null>(null);

  const handleQueueAudits = useCallback(async () => {
    if (!id || !list) return;
    const confirmed = window.confirm(
      `Audit all ${list.items.length} item${
        list.items.length === 1 ? "" : "s"
      } in "${list.name}" now?\n\nThis re-tests draft, pending and suspended codes — up to 10 per merchant — ahead of the schedule.`
    );
    if (!confirmed) return;

    setAuditing(true);
    setAuditResult(null);
    try {
      setAuditResult(await auditAllPost.execute(`qa-lists/${id}/audit`, {}));
    } catch (err) {
      console.error("Failed to queue audits:", err);
    } finally {
      setAuditing(false);
    }
  }, [id, list, auditAllPost]);

  const handleToggleFlag = useCallback(
    async (item: IQaReviewListItem, flag: string) => {
      const current = Array.isArray(item.flags) ? item.flags : [];
      const next = current.includes(flag)
        ? current.filter((f) => f !== flag)
        : [...current, flag];
      try {
        await setFlagsPut.execute(`qa-lists/${id}/items/${item._id}/flags`, {
          flags: next,
        });
        refetchList();
      } catch (err) {
        console.error("Failed to toggle flag:", err);
      }
    },
    [id, refetchList, setFlagsPut]
  );

  const openEditList = useCallback(() => {
    setDraftName(list?.name ?? "");
    setItemSearch("");
    setShowEditList(true);
  }, [list]);

  const handleRenameList = async () => {
    const name = draftName.trim();
    if (!id || !name || name === list?.name) return;
    setSavingName(true);
    try {
      await renamePut.execute(`qa-lists/${id}`, { name });
      refetchList();
    } catch (err) {
      console.error("Failed to rename the list:", err);
    } finally {
      setSavingName(false);
    }
  };

  const handleAddItems = async () => {
    if (newItems.length === 0) return;
    try {
      await addItemsPost.execute(`qa-lists/${id}/items`, { items: newItems });
      setNewItems([]);
      refetchList();
    } catch (err) {
      console.error("Failed to add items:", err);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    setRemovingItem(itemId);
    try {
      await deleteItemReq.execute(`qa-lists/${id}/items/${itemId}`);
      refetchList();
    } catch (err) {
      console.error("Failed to remove item:", err);
    } finally {
      setRemovingItem(null);
    }
  };

  // Available countries from the list items
  const countries = useMemo(() => {
    if (!list) return [];
    return Array.from(new Set(list.items.map((i) => i.countryCode))).sort();
  }, [list]);

  const editListItems = useMemo(() => {
    const items = list?.items ?? [];
    const q = itemSearch.trim().toLowerCase().replace(/^https?:\/\//, "");
    if (!q) return items;
    return items.filter(
      (i) =>
        i.domain.toLowerCase().includes(q) ||
        (i.countryCode ?? "").toLowerCase() === q
    );
  }, [list, itemSearch]);

  const handleSelectItem = useCallback((item: IQaReviewListItem) => {
    setSelectedItem(item);
    setSelectedEntity(null);
  }, []);

  const handleSelectEntity = useCallback(
    (type: "deal" | "coupon" | "offer", entityId: string) => {
      setSelectedEntity({ type, id: entityId });
    },
    []
  );

  const handleMarkTested = useCallback(
    async (comment: string) => {
      if (!selectedItem) return;
      setMarkingTested(selectedItem._id);
      try {
        await markTestedPost.execute(
          `qa-lists/${id}/items/${selectedItem._id}/mark-tested`,
          { comment }
        );
        refetchList();
      } catch (err) {
        console.error("Failed to mark tested:", err);
      } finally {
        setMarkingTested(null);
      }
    },
    [id, selectedItem, markTestedPost, refetchList]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!list) {
    return <div className="text-center py-5 text-muted">List not found</div>;
  }

  const showPanel3 = selectedEntity !== null;
  const selectedDomainSummary = selectedItem
    ? `${selectedItem.domain}${selectedItem.countryCode ? ` (${selectedItem.countryCode})` : ""}`
    : "No domain selected";

  return (
    <>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <Link to="/qa-lists" className="text-decoration-none">
            <ArrowLeft size={18} />
          </Link>
          <h5 className="mb-0">{list.name}</h5>
          <Badge bg="secondary" className="small">
            {list.assigneeName}
          </Badge>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} className="me-1" />
            Create New
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={openEditList}
          >
            <Edit size={14} className="me-1" />
            Edit List
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleBulkAddFullAutoFlag}
            disabled={bulkFlagging}
            title="Add the Full Auto Coupons Targeting flag to every item in this list"
          >
            <Smartphone size={14} className="me-1" />
            {bulkFlagging ? "Flagging..." : "Enlist all"}
          </Button>
          {/* Run-now is the button, cadence is behind the arrow — the two are
              the same job at different urgency, so they belong together. */}
          <Dropdown as={ButtonGroup}>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleQueueCaptures}
              disabled={capturing || list.items.length === 0}
              title="Queue the map template capture for every item in this list — the runner picks them up on its next poll"
            >
              <Play size={14} className="me-1" />
              {capturing ? "Queueing..." : "Capture all"}
            </Button>
            <Dropdown.Toggle
              split
              size="sm"
              variant="outline-primary"
              id="capture-actions"
            />
            <Dropdown.Menu align="end">
              <Dropdown.Header>Capture</Dropdown.Header>
              <Dropdown.Item onClick={openCaptureModal}>
                <Clock size={14} className="me-2" />
                Schedule…
                {listCron ? (
                  <Badge bg="primary" className="ms-2">
                    {customCron ? "custom" : `${cronDays?.length ?? 0}d`}
                  </Badge>
                ) : (
                  <Badge bg="light" text="dark" className="ms-2">
                    global
                  </Badge>
                )}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown as={ButtonGroup}>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleQueueAudits}
              disabled={auditing || list.items.length === 0}
              title="Re-test every item's draft, pending and suspended codes now, ahead of the audit schedule"
            >
              <Play size={14} className="me-1" />
              {auditing ? "Queueing..." : "Audit all"}
            </Button>
            <Dropdown.Toggle
              split
              size="sm"
              variant="outline-primary"
              id="audit-actions"
            />
            <Dropdown.Menu align="end">
              <Dropdown.Header>Audit</Dropdown.Header>
              <Dropdown.Item onClick={openAuditModal}>
                <Clock size={14} className="me-2" />
                Schedule…
                {listAuditCron ? (
                  <Badge bg="primary" className="ms-2">
                    {customAuditCron ? "custom" : `${auditDays?.length ?? 0}d`}
                  </Badge>
                ) : (
                  <Badge bg="light" text="dark" className="ms-2">
                    off
                  </Badge>
                )}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {editCount > 0 && (
            <Button
              variant="warning"
              size="sm"
              onClick={() => setShowReview(true)}
            >
              <Edit2 size={14} className="me-1" />
              Review Changes
              <Badge bg="dark" className="ms-1">
                {editCount}
              </Badge>
            </Button>
          )}
        </div>
      </div>

      {auditResult && (
        <Alert
          variant={auditResult.unmatched > 0 ? "warning" : "success"}
          dismissible
          onClose={() => setAuditResult(null)}
          className="py-2"
        >
          <div className="small">
            Queued <strong>{auditResult.queued}</strong> of{" "}
            {auditResult.requested} item
            {auditResult.requested === 1 ? "" : "s"} for audit. The scheduler
            picks them up on its next poll.
            {auditResult.created > 0 && (
              <>
                {" "}
                <strong>{auditResult.created}</strong> had no merchant yet and
                were created — those build a template first, then audit.
              </>
            )}
            {auditResult.unmatched > 0 && (
              <>
                {" "}
                <strong>{auditResult.unmatched}</strong> could not be added
                (duplicate or unusable domain).
              </>
            )}
          </div>
        </Alert>
      )}

      {captureResult && (
        <Alert
          variant={captureResult.unmatched.length > 0 ? "warning" : "success"}
          dismissible
          onClose={() => setCaptureResult(null)}
          className="py-2"
        >
          <div className="small">
            Queued <strong>{captureResult.queued}</strong> of{" "}
            {captureResult.requested} item
            {captureResult.requested === 1 ? "" : "s"} for map template capture.
            The runner picks them up on its next poll.
          </div>
          {captureResult.created.length > 0 && (
            <div className="small mt-1">
              Added <strong>{captureResult.created.length}</strong> new merchant
              {captureResult.created.length === 1 ? "" : "s"} for:{" "}
              <strong>
                {captureResult.created
                  .map((c) => `${c.domain} (${c.countryCode})`)
                  .join(", ")}
              </strong>
            </div>
          )}
          {captureResult.unmatched.length > 0 && (
            <div className="small mt-1">
              Could not be matched or added, so nothing was queued for:{" "}
              <strong>
                {captureResult.unmatched
                  .map((u) => `${u.domain} (${u.countryCode})`)
                  .join(", ")}
              </strong>
            </div>
          )}
        </Alert>
      )}

      {/* Three-panel layout */}
      <div
        className="d-flex border rounded"
        style={{ height: "calc(100vh - 165px)", overflow: "hidden" }}
      >
        {/* Panel 1: Item list */}
        <div
          className="border-end"
          style={{
            width: isListPanelCollapsed ? 160 : showPanel3 ? "22%" : "28%",
            minWidth: isListPanelCollapsed ? 140 : 220,
            transition: "width 0.2s",
          }}
        >
          {isListPanelCollapsed ? (
            <div className="d-flex flex-column h-100 p-2 bg-light">
              <div className="d-flex justify-content-end mb-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsListPanelCollapsed(false)}
                  title="Expand list"
                >
                  <ChevronsRight size={14} />
                </Button>
              </div>
              <div className="small text-muted mb-1">Selected</div>
              <div
                className="small fw-semibold text-truncate"
                title={selectedDomainSummary}
              >
                {selectedDomainSummary}
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-end p-2 border-bottom bg-light">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsListPanelCollapsed(true)}
                  title="Collapse list"
                >
                  <ChevronsLeft size={14} />
                </Button>
              </div>
              <ReviewItemList
                items={list.items}
                selectedItemId={selectedItem?._id ?? null}
                onSelectItem={handleSelectItem}
                searchFilter={searchFilter}
                countryFilter={countryFilter}
                untestedOnly={untestedOnly}
                notTestedDays={notTestedDays}
                onSearchChange={setSearchFilter}
                onCountryChange={setCountryFilter}
                onUntestedChange={setUntestedOnly}
                onNotTestedDaysChange={setNotTestedDays}
                countries={countries}
                onToggleFlag={handleToggleFlag}
                fullAutoFlagFilter={fullAutoFlagFilter}
                onFullAutoFlagFilterChange={setFullAutoFlagFilter}
              />
            </>
          )}
        </div>

        {/* Panel 2: Data tabs */}
        <div
          className="d-flex flex-column"
          style={{
            flex: 1,
            minWidth: 0,
            borderRight: showPanel3 ? "1px solid #dee2e6" : undefined,
          }}
        >
          <ReviewDataPanel
            data={domainGeoData ?? null}
            loading={domainGeoLoading}
            selectedDomain={selectedItem?.domain ?? null}
            selectedCountryCode={selectedItem?.countryCode ?? null}
            selectedEntity={selectedEntity}
            onSelectEntity={handleSelectEntity}
            onMarkTested={handleMarkTested}
            markingTested={markingTested !== null}
            lastTestedAt={selectedItem?.lastTestedAt ?? null}
            testedByName={selectedItem?.testedByName ?? null}
          />
        </div>

        {/* Panel 3: Entity detail (conditional) */}
        {showPanel3 && (
          <div
            style={{
              width: "35%",
              minWidth: 300,
              transition: "width 0.2s",
            }}
          >
            <ReviewDetailPanel
              entityType={selectedEntity?.type ?? null}
              entityId={selectedEntity?.id ?? null}
              countryCode={selectedItem?.countryCode ?? null}
              onClose={() => setSelectedEntity(null)}
            />
          </div>
        )}
      </div>

      {/* Review modal */}
      <ReviewSubmitModal
        show={showReview}
        onHide={() => setShowReview(false)}
        onSuccess={() => {
          setShowReview(false);
          refetchList();
        }}
      />

      {/* Create entity modal */}
      <CreateEntityModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        onCreated={() => {
          /* entity created — no special action needed */
        }}
      />

      {/* Edit list modal */}
      <Modal
        show={showCaptureModal}
        onHide={() => setShowCaptureModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Capture schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customCron ? (
            <Alert variant="warning" className="mb-0">
              This list uses a schedule the picker cannot represent:{" "}
              <code>{listCron}</code>. Editing here would replace it, so change
              it through the API instead.
            </Alert>
          ) : (
            <>
              <Form.Check
                type="switch"
                id="capture-schedule-enabled"
                className="mb-3"
                checked={draftEnabled}
                onChange={(e) => setDraftEnabled(e.target.checked)}
                label="Use a schedule for this list"
              />
              <div className="text-muted small mb-2">
                {draftEnabled
                  ? "Templates for this list are re-captured at midnight UTC on the days selected."
                  : "Off — this list follows the global capture cadence."}
              </div>
              <ButtonGroup className="w-100">
                {CAPTURE_DAYS.map((d) => (
                  <Button
                    key={d.cron}
                    variant={
                      draftDays.includes(d.cron)
                        ? "primary"
                        : "outline-secondary"
                    }
                    disabled={!draftEnabled}
                    onClick={() => toggleDraftDay(d.cron)}
                  >
                    {d.label}
                  </Button>
                ))}
              </ButtonGroup>
              {draftEnabled && !draftDays.length && (
                <div className="text-muted small mt-2">
                  No days selected — saving now turns the schedule off.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCaptureModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveCaptureSchedule}
            disabled={savingCron || customCron}
          >
            {savingCron ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Audit schedule — re-testing pending and suspended codes */}
      <Modal
        show={showAuditModal}
        onHide={() => setShowAuditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Audit schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customAuditCron ? (
            <Alert variant="warning" className="mb-0">
              This list uses a schedule the picker cannot represent:{" "}
              <code>{listAuditCron}</code>. Editing here would replace it, so
              change it through the API instead.
            </Alert>
          ) : (
            <>
              <Form.Check
                type="switch"
                id="audit-schedule-enabled"
                className="mb-3"
                checked={draftAuditEnabled}
                onChange={(e) => setDraftAuditEnabled(e.target.checked)}
                label="Audit this list's pending and suspended codes"
              />
              <div className="text-muted small mb-2">
                {draftAuditEnabled
                  ? `Draft, pending and suspended codes are re-tested at ${String(draftAuditHour).padStart(2, "0")}:00 UTC on the days selected. A code that keeps failing is retried less often — 7, 14, 28, 56 then 112 days.`
                  : "Off — this list's non-live codes are never re-tested."}
              </div>
              <Form.Group className="mb-3" controlId="audit-schedule-hour">
                <Form.Label className="small mb-1">Start time (UTC)</Form.Label>
                <Form.Select
                  size="sm"
                  value={draftAuditHour}
                  disabled={!draftAuditEnabled}
                  onChange={(e) => setDraftAuditHour(Number(e.target.value))}
                  title="Hour the audit becomes due; merchants are claimed from then as runner capacity allows"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <ButtonGroup className="w-100">
                {CAPTURE_DAYS.map((d) => (
                  <Button
                    key={d.cron}
                    variant={
                      draftAuditDays.includes(d.cron)
                        ? "primary"
                        : "outline-secondary"
                    }
                    disabled={!draftAuditEnabled}
                    onClick={() => toggleDraftAuditDay(d.cron)}
                  >
                    {d.label}
                  </Button>
                ))}
              </ButtonGroup>
              {draftAuditEnabled && !draftAuditDays.length && (
                <div className="text-muted small mt-2">
                  No days selected — saving now turns the schedule off.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowAuditModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveAuditSchedule}
            disabled={savingAudit || customAuditCron}
          >
            {savingAudit ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditList}
        onHide={() => setShowEditList(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="mb-2">List Name</h6>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              size="sm"
              value={draftName}
              maxLength={200}
              placeholder="List name"
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleRenameList();
                }
              }}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleRenameList}
              disabled={
                savingName ||
                !draftName.trim() ||
                draftName.trim() === list.name
              }
            >
              {savingName ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Rename"
              )}
            </Button>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
            <h6 className="mb-0 text-nowrap">
              Current Items (
              {itemSearch.trim()
                ? `${editListItems.length} of ${list.items.length}`
                : list.items.length}
              )
            </h6>
            <InputGroup size="sm" style={{ maxWidth: 280 }}>
              <InputGroup.Text>
                <Search size={12} />
              </InputGroup.Text>
              <Form.Control
                value={itemSearch}
                placeholder="Search domain or country"
                title="Check whether a domain is on this list, or find it to remove it"
                onChange={(e) => setItemSearch(e.target.value)}
              />
            </InputGroup>
          </div>
          <div style={{ maxHeight: 250, overflow: "auto" }} className="mb-3">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Country</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {itemSearch.trim() && editListItems.length === 0 && (
                  <tr>
                    <td colSpan={3} className="small text-muted">
                      No domain matching "{itemSearch.trim()}" is on this list.
                    </td>
                  </tr>
                )}
                {editListItems.map((item) => (
                  <tr key={item._id}>
                    <td className="small">{item.domain}</td>
                    <td className="small">{item.countryCode || "—"}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={removingItem === item._id}
                        onClick={() => handleRemoveItem(item._id)}
                      >
                        {removingItem === item._id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Trash2 size={12} />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <h6 className="mb-2">Add Items</h6>
          <DomainGeoPicker selected={newItems} onChange={setNewItems} />
          {newItems.length > 0 && (
            <Button
              variant="success"
              size="sm"
              className="mt-2"
              onClick={handleAddItems}
              disabled={addItemsPost.loading}
            >
              {addItemsPost.loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Plus size={14} className="me-1" />
                  Add {newItems.length} Item{newItems.length > 1 ? "s" : ""}
                </>
              )}
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditList(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const QaReview: React.FC = () => {
  return (
    <EditSessionProvider>
      <QaReviewInner />
    </EditSessionProvider>
  );
};
